import { FormItem } from "./form-permissions-item/item.model";
import { TableItem } from "./column/column.model";
import {
  Customer,
  Department,
  Installation,
  OrderType,
  Product,
  ProductCategory,
  Status,
} from "../master-data/master-data.model";
import {
  DeviceAttachmentGroup,
  InteractiveAttachmentGroup,
} from "src/app/forms/forms.model";

export enum FormAssignmentType {
  ORDER_TYPE,
  PRODUCT_CATEGORY,
  PRODUCT,
  CUSTOMER,
  INSTALLATION,
}

export enum PermissionType {
  STATUS = "status",
  CUSTOMER = "cust",
  INSTALLATION = "inst",
  ORDER_TYPE = "type",
  BRANCH = "branch",
  DEPARTMENT = "dep",
  PRODUCT = "prod",
  PRODUCT_CATEGORY = "prodCat",
  LANGUAGE = "lang",
}

export enum ActionType {
  EDIT = "edit",
  SAVE = "save",
}

export class PermissionObject {
  permissionType: PermissionType;
  name: string;
  objUuid: string;

  constructor(permissionT: PermissionType, name: string, oUuid: string) {
    this.permissionType = permissionT;
    this.name = name;
    this.objUuid = oUuid;
  }
}

export class PermissionStringMap {
  included: string[][];
  excluded: string[];
}

export class PermissionMap {
  included: PermissionObject[][];
  excluded: PermissionObject[];
}

export class PermissionInfo {
  edit: PermissionMap;
  save: PermissionMap;
}

export class ChangeFormPermissionsCommand {
  templateUuid: string;
  permissions: Map<ActionType, PermissionStringMap>;
}

export interface FormAssignmentAPI {
  formUuid: string;
  objectType: number;
  objectUuids: string[];
}

export interface FormAllObjectsCommandAPI {
  formUuid: string;
  allObjects: boolean;
}

export interface FormSetActiveCommandAPI {
  formUuid: string;
  active: boolean;
}

export interface UpdateFormFileCommand {
  formUuid: string;
  templateBase64: string;
}

export class FormTemplate {
  uuid?: string | null;
  title: string;
  fields: FormItem[];
  groups: FormSection[];
  tables: FormTable[];
  signatures?: FormSignature[];
  attachments?: FormAttachment[];
  attachmentGroups?: ServiceAttachmentGroup[];
  interactiveAttachmentGroups?: InteractiveAttachmentGroup[];
  deviceAttachmentGroups?: DeviceAttachmentGroup[];
  drawings?: FormDrawing[];
  templateBase64?: string;
  types?: OrderType[];
  departments?: Department[];
  productCategories?: ProductCategory[];
  customers?: Customer[];
  installations?: Installation[];
  products?: Product[];
  statuses: Status[];
  active: boolean;
  allObjects: boolean;
  allStatuses: boolean;
  signatureRequired: boolean;
  multiFormAllowed: boolean;
  permissions: Map<ActionType, PermissionStringMap>;

  constructor(
    title?: string,
    groups?: FormSection[],
    fields?: FormItem[],
    tables?: FormTable[],
    uuid?: string,
  ) {
    this.title = title;
    this.fields = fields;
    this.groups = groups;
    this.tables = tables;
    this.uuid = uuid;
    this.types = [];
    this.productCategories = [];
    this.customers = [];
    this.installations = [];
    this.products = [];
    this.permissions = null;
  }
}

export class DescriptiveFormTemplate {
  uuid: string;
  title: string;
  active: boolean;

  constructor(uuid: string, title: string, active: boolean) {
    this.uuid = uuid;
    this.title = title;
    this.active = active;
  }
}

export class FormSection {
  name: string;
  fields?: FormItem[];
  sort?: number;

  constructor(name: string, fields?: FormItem[]) {
    this.name = name;
    this.fields = fields && [...fields];
  }
}

export class FormTable {
  name: string;
  renderTag: string;
  columns?: TableItem[];
  sort?: number;

  constructor(name: string, renderTag: string, columns?: TableItem[]) {
    this.name = name;
    this.renderTag = renderTag;
    this.columns = columns && [...columns];
  }
}

export class FormSignature {
  name: string;
  optional: boolean;
  renderTag: string;
  sort?: number;

  constructor(name: string, tag?: string, optional?: boolean, sort?: number) {
    this.name = name;
    this.renderTag = tag;
    this.optional = optional ?? false;
    this.sort = sort ?? 0;
  }
}

export class FormDrawing {
  name: string;
  optional: boolean;
  renderTag: string;
  sort?: number;
  base64PngBackground: string;
  blankBkg?: boolean;

  constructor(
    name: string,
    tag: string,
    blankBkg = true,
    background?: string,
    optional?: boolean,
    sort?: number,
  ) {
    this.name = name;
    this.renderTag = tag;
    this.blankBkg = blankBkg;
    this.base64PngBackground = background ?? null;
    this.optional = optional ?? false;
    this.sort = sort ?? 0;
  }
}

export class FormAttachment {
  name: string;
  newPage: boolean;
  optional: boolean;
  renderTag: string;
  templateUuid: string;
  sort?: number;
  filled?: boolean;

  constructor(
    name: string,
    tag?: string,
    childTemplate?: string,
    newPage?: boolean,
    optional?: boolean,
    filled?: boolean,
    sort?: number,
  ) {
    this.name = name;
    this.renderTag = tag;
    this.templateUuid = childTemplate;
    this.newPage = newPage ?? false;
    this.optional = optional ?? false;
    this.filled = filled ?? false;
    this.sort = sort ?? 0;
  }
}

export enum ServiceAttachmentGroupType {
  REQUIRED_ONE,
  REQUIRED_ONE_OR_MORE,
  REQUIRED_ALL,
  ALL_OPTIONAL,
}

export class ServiceAttachmentGroup {
  name: string;
  renderTag: string;
  sort?: number;
  type: ServiceAttachmentGroupType;
  attachments: FormAttachment[];

  constructor(
    name: string,
    tag?: string,
    sort?: number,
    type?: ServiceAttachmentGroupType,
    attachments?: FormAttachment[],
  ) {
    this.name = name;
    this.renderTag = tag;
    this.sort = sort ?? 0;
    this.type = type;
    this.attachments = attachments ?? [];
  }
}

export interface RawPermissions {
  edit?: { included: string[][]; excluded: string[] };
  save?: { included: string[][]; excluded: string[] };
}

export interface ViewPermission {
  type: string;
  uuid: string;
  name: string;
  raw: string;
}
